import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef, useMemo } from "react";
import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";
import { getP2PChatsFromLocalDB } from "@/indexedDB/services/chats";

import NewChatMenu from "../NewChatMenu";
import SearchChatInput from "../SearchChatInput";
import { ChatUserAction } from "@/zustand/NavigationState";
import ChatListEntry, { CHAT_LIST_ITEM_HEIGHT } from "../ChatListEntry";
import ArchivedChatsSection from "../ArchivedChatsSection";
import { useTranslation } from "react-i18next";

export default function P2PChatListView() {
  const { t } = useTranslation();
  const parentRef = useRef<HTMLDivElement>(null);
  const chats = useSafeLiveQuery(() => getP2PChatsFromLocalDB(), []);

  const { sortedRegularChats, archivedChats } = useMemo(() => {
    const regular = chats?.filter((chat) => !chat.isArchived) || [];
    const archived = chats?.filter((chat) => chat.isArchived) || [];

    const sorted = [...regular].sort((a, b) => {
      if (a.isPinned === b.isPinned) {
        return 0;
      }
      return a.isPinned ? -1 : 1;
    });

    return { sortedRegularChats: sorted, archivedChats: archived };
  }, [chats]);

  const rowVirtualizer = useVirtualizer({
    count: sortedRegularChats.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CHAT_LIST_ITEM_HEIGHT,
    overscan: 5,
  });

  if (!chats) {
    return null;
  }
  return (
    <div className="relative flex size-full flex-col overflow-hidden">
      <div className="flex h-leftColumnHeaderHeight shrink-0 items-center border-b border-primary/20 px-4">
        <h1 className="text-lg font-semibold">{t("directMessages")}</h1>
        <NewChatMenu />
      </div>
      <SearchChatInput searchAction={ChatUserAction.SEARCH_P2P_CHAT} />

      {sortedRegularChats.length === 0 && archivedChats.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center">
          <h2 className="text-center text-lg font-semibold">{t("noChats")}</h2>
          <p className="text-center text-sm text-muted-2">
            {t("noChatsDescription")}
          </p>
        </div>
      )}

      <div ref={parentRef} className="flex-1 overflow-y-auto p-2">
        {sortedRegularChats.length > 0 && (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const chat = sortedRegularChats[virtualRow.index];
              return (
                <div
                  key={chat.id}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <ChatListEntry chat={chat} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Archived chats section */}
      <ArchivedChatsSection archivedChats={archivedChats} />
    </div>
  );
}
